/**
 * HeirWise Sim Wallet — Weekly Payroll
 *
 * Centralized, simplified payroll math for the learner Sim Wallet.
 * This is intentionally NOT a real-world payroll engine — it teaches the
 * difference between gross pay, withholding, deductions, and take-home pay.
 *
 * Rules (v1):
 *   - Pay frequency is weekly only.
 *   - Taxes/deductions are only applied on the weekly paycheck.
 *   - Tax rates are simplified bands keyed off annual salary.
 */

export type IncomeStreamKind =
  | "career_job"
  | "part_time"
  | "side_hustle"
  | "allowance_gift";

export interface WeeklyPaycheckBreakdown {
  annualSalary: number;
  weeklyGrossPay: number;
  estimatedTaxRate: number; // 0..1
  taxesWithheld: number;
  benefitsDeduction: number;
  netPay: number;
}

/** Annual salary -> weekly gross. */
export function calculateWeeklyGrossPay(annualSalary: number): number {
  if (!Number.isFinite(annualSalary) || annualSalary <= 0) return 0;
  return round2(annualSalary / 52);
}

/**
 * Simplified estimated withholding rate.
 * Low income: 12–15% · Middle: 18–24% · High: 25–32%
 */
export function estimateTaxRate(annualSalary: number): number {
  if (annualSalary <= 0) return 0;
  if (annualSalary < 35_000) return 0.13;
  if (annualSalary < 60_000) return 0.18;
  if (annualSalary < 100_000) return 0.22;
  if (annualSalary < 175_000) return 0.27;
  return 0.31;
}

export function calculateTaxWithholding(grossPay: number, taxRate: number): number {
  return round2(Math.max(0, grossPay) * Math.max(0, Math.min(1, taxRate)));
}

export function calculateNetPay(
  grossPay: number,
  taxes: number,
  deductions: number
): number {
  return round2(Math.max(0, grossPay - taxes - deductions));
}

/**
 * Default benefits/retirement deduction (~5% of gross, capped).
 * Kept tiny and predictable in v1; future versions can let learners tune it.
 */
export function defaultBenefitsDeduction(grossPay: number): number {
  if (grossPay <= 0) return 0;
  return round2(Math.min(grossPay * 0.05, 250));
}

/** Full breakdown for an annual salary. */
export function buildWeeklyPaycheck(annualSalary: number): WeeklyPaycheckBreakdown {
  const weeklyGrossPay = calculateWeeklyGrossPay(annualSalary);
  const estimatedTaxRate = estimateTaxRate(annualSalary);
  const taxesWithheld = calculateTaxWithholding(weeklyGrossPay, estimatedTaxRate);
  const benefitsDeduction = defaultBenefitsDeduction(weeklyGrossPay);
  const netPay = calculateNetPay(weeklyGrossPay, taxesWithheld, benefitsDeduction);
  return {
    annualSalary,
    weeklyGrossPay,
    estimatedTaxRate,
    taxesWithheld,
    benefitsDeduction,
    netPay,
  };
}

/** ISO-week key (e.g. "2026-W26") used to make payday idempotent. */
export function isoWeekKey(d: Date = new Date()): string {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

/** Next Friday at 9am local — friendly "next payday" anchor. */
export function nextPayday(from: Date = new Date()): Date {
  const d = new Date(from);
  const day = d.getDay(); // 0 Sun..6 Sat, Friday=5
  const diff = (5 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  d.setHours(9, 0, 0, 0);
  return d;
}

export function paycheckReason(weekKey: string): string {
  return `Career paycheck (${weekKey})`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}